/**
 * Story 14.4 (AC-4.3, AC-4.7) — browser-side Sentry init.
 *
 * File name + load mechanism (#87 fix, 2026-05-31): this file MUST be named
 * `instrumentation-client.ts` at the frontend root. Next.js 15.3+ auto-loads
 * it for the browser runtime — which is what `@sentry/nextjs` v8+ expects.
 * The pre-v8 convention was `sentry.client.config.ts` auto-injected by
 * `withSentryConfig`, but that mechanism was removed in v8. Leaving the old
 * filename here meant `Sentry.init` was never bundled and frontend monitoring
 * was silently dead despite the SDK code being in the package.
 *
 * The three-file pattern is:
 *   - `instrumentation.ts` (server + edge runtimes via `register()`)
 *   - `instrumentation-client.ts` (THIS file — browser runtime, auto-loaded)
 *   - `withSentryConfig` wrapping `next.config.ts` (source-map upload only)
 *
 * `sendDefaultPii: false` mirrors the backend's `send_default_pii=False`:
 *   IP / headers / cookies are NEVER attached to events. Tenant tagging is
 *   handled by the `<SentryAuthSync />` Client Component mounted inside
 *   ClerkProvider in `src/app/layout.tsx`; that component reads
 *   `useOrganization().publicMetadata.customer_id` and tags the active scope.
 *   Pass-2 m7 denylist: no email, no org_id, no Sentry.setUser.
 *
 * Init is a no-op when `NEXT_PUBLIC_SENTRY_DSN` is empty (dev / unit-test).
 */

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/emails/:id\",\"regex\":\"^/emails/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/emails/:id/match-review\",\"regex\":\"^/emails/([^/]+)/match-review$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/emails/:id/pi-preview\",\"regex\":\"^/emails/([^/]+)/pi-preview$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/sign-in/:sign-in*?\",\"regex\":\"^/sign-in(?:/(.*))?$\",\"paramNames\":[\"sign-in\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/admin/llm-costs\"},{\"path\":\"/admin/onboarding\"},{\"path\":\"/emails\"},{\"path\":\"/inventory\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.3.8";import * as Sentry from '@sentry/nextjs';

/**
 * CR P6: clamp the parsed rate to [0, 1] and fall back to 0.05 on NaN / parse
 * failure. Pre-CR: `Number('high')` returned `NaN` → undefined Sentry behaviour;
 * `'5'` (operator meaning 5%) was accepted as 5.0 → some SDK versions sample 500%
 * = always-on, exploding quota.
 */
function safeRate(raw: string | undefined, fallback = 0.05): number {
  const parsed = Number(raw ?? fallback);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(1, Math.max(0, parsed));
}

if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ?? 'development',
    tracesSampleRate: safeRate(process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE),
    sendDefaultPii: false,
  });
}
